import * as React from 'react'
import { useLocation } from '@tanstack/react-router'

/**
 * Safety net for a stuck `body { pointer-events: none }`.
 *
 * Radix locks pointer events on the body while a modal layer (dialog, dropdown,
 * context menu, select) is open, and unlocks on close. If a close is
 * interrupted — a fast re-click, a route change mid-animation, a trigger
 * unmounting while its menu closes — the lock can survive its owner.
 *
 * The symptom is app-wide and easy to misread: nothing is hoverable, so every
 * control shows a default arrow instead of a pointer, and the cursor appears to
 * "flash" pointer and revert as elements re-render underneath.
 *
 * This watches for the lock and releases it whenever no layer is actually open.
 * The delay matters: Radix sets the lock slightly before its content mounts, so
 * checking synchronously would clear a legitimate lock and break modality.
 */
export function PointerEventsGuard() {
  const pathname = useLocation({ select: (location) => location.pathname })

  React.useEffect(() => {
    const OPEN_LAYER = [
      '[data-radix-popper-content-wrapper]',
      '[role="dialog"][data-state="open"]',
      '[role="menu"][data-state="open"]',
      '[data-state="open"][data-slot="dialog-content"]',
      '[data-sonner-toast]',
    ].join(',')

    let timer: ReturnType<typeof setTimeout> | undefined

    const release = () => {
      if (document.body.style.pointerEvents !== 'none') return
      if (document.querySelector(OPEN_LAYER)) return // a real layer owns the lock
      document.body.style.removeProperty('pointer-events')
    }

    const schedule = () => {
      clearTimeout(timer)
      // Let Radix finish mounting its layer before judging the lock orphaned.
      timer = setTimeout(release, 150)
    }

    const observer = new MutationObserver(schedule)
    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] })

    // Also sweep on navigation: a trigger can unmount while its menu closes.
    schedule()

    return () => {
      clearTimeout(timer)
      observer.disconnect()
    }
  }, [pathname])

  return null
}
